import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Twitter, Github, Linkedin, Send, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser'; // Import EmailJS
import Navbar from './components/Navbar'; 
import Footer from './components/Footer';

const ConnectWithUs = () => {
  const [formData, setFormData] = useState({ fullName: '', email: '', organization: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      /* --- Keyframe Animations --- */
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      /* --- Hover & Focus Effects --- */
      [data-component="contact-item"]:hover {
        background-color: #f7fafc;
        transform: translateX(5px);
      }
      [data-component="social-icon"]:hover {
        transform: translateY(-3px) scale(1.1);
        box-shadow: 0 8px 25px rgba(20, 184, 166, 0.4);
      }
      [data-component="section"]:hover {
        transform: translateY(-5px);
        box-shadow: 0 25px 50px rgba(0,0,0,0.15);
      }
      [data-component="submit-btn"]:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(20, 184, 166, 0.4);
      }

      /* --- Responsive Design --- */
      @media (max-width: 992px) {
        [data-component="content-container"] {
          grid-template-columns: 1fr !important;
          gap: 40px !important;
        }
      }
      
      @media (max-width: 768px) {
        [data-component="container"] {
          padding: 40px 15px !important;
        }
        [data-component="main-title"] {
          font-size: 36px !important;
        }
        [data-component="section"] {
          padding: 30px 20px !important;
        }
        [data-component="google-map"] {
          height: 250px !important;
        }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Updated handleSubmit to send email via EmailJS
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.message) {
      alert("Please fill out all required fields.");
      return;
    }
    setIsSubmitting(true);

    // --- IMPORTANT: Replace with your actual EmailJS credentials ---
    const serviceID = 'service_n36e8hg'; // Your EmailJS Service ID
    const templateID = 'template_i9jkfri'; // Your EmailJS Template ID
    const publicKey = 'VAEdCMXuUKeCOw6gb'; // Your EmailJS Public Key

    // Construct the email body from the form data
    const emailBody = `
      You have received a new message from the 'Connect With Us' form.
      -----------------------------------------------------------------

      Sender Information:
      - Full Name: ${formData.fullName}
      - Email Address: ${formData.email}
      - Organization: ${formData.organization || 'Not provided'}

      Message:
      ${formData.message}
    `;

    // Prepare the parameters for your EmailJS template
    const templateParams = {
      email_subject: "General Inquiry", // Set the subject as requested
      email_body: emailBody.trim()
    };

    // Send the email
    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setIsSubmitting(false);
        setIsSubmitted(true);
        // Reset form after a delay
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ fullName: '', email: '', organization: '', message: '' });
        }, 3000);
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('Failed to send the message. Please try again later.');
        setIsSubmitting(false);
      });
  };
  
  // Base styles object (no changes)
  const styles = {
    container: {
      minHeight: '100vh',
      background: '#ffffff',
      padding: '60px 20px',
      fontFamily: 'Arial, sans-serif'
    },
    maxWidth: {
      maxWidth: '1200px',
      margin: '6em auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '50px'
    },
    mainTitle: {
      fontSize: '48px',
      fontWeight: '700',
      marginBottom: '20px',
      color: '#14b8a6'
    },
    subtitle: {
      fontSize: '18px',
      color: '#6b7280',
      maxWidth: '600px',
      margin: '0 auto',
      lineHeight: '1.6'
    },
    contentContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '40px',
      alignItems: 'flex-start'
    },
    section: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      padding: '40px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s ease'
    },
    sectionTitle: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '30px'
    },
    contactItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '15px',
      marginBottom: '25px',
      padding: '15px',
      borderRadius: '12px',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    iconContainer: {
      width: '50px',
      height: '50px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#14b8a6',
      color: 'white',
      flexShrink: 0,
      boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)'
    },
    contactLabel: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '4px'
    },
    contactValue: {
      fontSize: '14px',
      color: '#718096',
      lineHeight: '1.5'
    },
    socialContainer: {
      display: 'flex',
      gap: '15px',
      marginTop: '20px'
    },
    socialIcon: {
      width: '45px',
      height: '45px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#14b8a6',
      color: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)'
    },
    formGroup: {
      marginBottom: '25px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '8px'
    },
    input: (fieldName) => ({
      width: '100%',
      padding: '15px 18px',
      border: `2px solid ${focusedField === fieldName ? '#14b8a6' : '#e2e8f0'}`,
      borderRadius: '12px',
      fontSize: '16px',
      transition: 'all 0.3s ease',
      backgroundColor: focusedField === fieldName ? '#f0fdfa' : 'white',
      outline: 'none',
      boxShadow: focusedField === fieldName ? '0 0 0 3px rgba(20, 184, 166, 0.1)' : 'none',
      fontFamily: 'inherit'
    }),
    button: {
      width: '100%',
      padding: '15px',
      background: isSubmitting ? '#a0aec0' : '#14b8a6',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: isSubmitting ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)'
    },
    successMessage: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      padding: '15px',
      backgroundColor: '#d1fae5',
      color: '#065f46',
      borderRadius: '12px',
      marginTop: '20px',
      animation: 'fadeIn 0.5s ease-in-out'
    },
    loader: {
      width: '20px',
      height: '20px',
      border: '2px solid #ffffff',
      borderTop: '2px solid transparent',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  return (
    <>
      <Navbar /> 
      <div style={styles.container} data-component="container">
        <div style={styles.maxWidth}>
          <div style={styles.header}>
            <h1 style={styles.mainTitle} data-component="main-title">Connect with Us</h1>
            <p style={styles.subtitle}>
              Ready to join our global community? Get in touch to learn more about our programs and initiatives.
            </p>
          </div>

          <div style={styles.contentContainer} data-component="content-container">
            <div style={styles.section} data-component="section">
              <h2 style={styles.sectionTitle}>Contact Information</h2>
              
              <div style={styles.contactItem} data-component="contact-item" onClick={() => window.open('mailto:contact@globalaisociety.com')}>
                <div style={styles.iconContainer}><Mail size={20} /></div>
                <div>
                  <div style={styles.contactLabel}>Email</div>
                  <div style={styles.contactValue}>info@globalaisociety.com</div>
                </div>
              </div>

              <div style={styles.contactItem} data-component="contact-item" onClick={() => window.open('tel:+919900001511')}>
                <div style={styles.iconContainer}><Phone size={20} /></div>
                <div>
                  <div style={styles.contactLabel}>Phone</div>
                  <div style={styles.contactValue}>+91 99000 01511</div>
                </div>
              </div>

              <div style={styles.contactItem} data-component="contact-item">
                <div style={styles.iconContainer}><MapPin size={20} /></div>
                <div>
                  <div style={styles.contactLabel}>Address</div>
                  <div style={styles.contactValue}>
                    No. 261, 9th A Main Jayanagar 3rd Block<br />
                    Bengaluru - 560011, India
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '20px', borderRadius: '12px', overflow: 'hidden' }}>
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.611488744709!2d77.5843994!3d12.9326737!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae15980287fd83%3A0x3cd447112111fc17!2sImperial%20College!5e0!3m2!1sen!2sin!4v1752479492742!5m2!1sen!2sin" 
                  width="100%" 
                  height="120" 
                  style={{ border: 0 }}
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  data-component="google-map"
                />
              </div>

              <h3 style={{...styles.sectionTitle, fontSize: '24px', marginTop: '40px'}}>Follow Us</h3>
              <div style={styles.socialContainer}>
                <div style={styles.socialIcon} data-component="social-icon"><Twitter size={20} /></div>
                <div style={styles.socialIcon} data-component="social-icon"><Github size={20} /></div>
                <div style={styles.socialIcon} data-component="social-icon"><Linkedin size={20} /></div>
              </div>
            </div>

            <div style={styles.section} data-component="section">
              <h2 style={styles.sectionTitle}>Send us a Message</h2>
              <form onSubmit={handleSubmit}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Full Name</label>
                  <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} onFocus={() => setFocusedField('fullName')} onBlur={() => setFocusedField(null)} style={styles.input('fullName')} placeholder="Your Name" required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email Address</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} style={styles.input('email')} placeholder="your.email@example.com" required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Organization Name</label>
                  <input type="text" name="organization" value={formData.organization} onChange={handleInputChange} onFocus={() => setFocusedField('organization')} onBlur={() => setFocusedField(null)} style={styles.input('organization')} placeholder="Your Organization" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Message</label>
                  <textarea name="message" value={formData.message} onChange={handleInputChange} onFocus={() => setFocusedField('message')} onBlur={() => setFocusedField(null)} style={{...styles.input('message'), minHeight: '120px', resize: 'vertical'}} placeholder="Tell us about your interest..." required />
                </div>
                <button type="submit" style={styles.button} data-component="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? (<><div style={styles.loader} /><span>Sending...</span></>) : (<><Send size={20} /><span>Send Message</span></>)}
                </button>
                {isSubmitted && (<div style={styles.successMessage}><CheckCircle size={20} /><span>Message sent successfully!</span></div>)}
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer /> 
    </>
  );
};

export default ConnectWithUs;